<template>
  <section id="device-details">
    <span
      v-if="loadingData"
      class="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
      style="z-index: 1050"
    >
      <b-spinner
        variant="primary"
        style="width: 5rem; height: 5rem"
      />
    </span>
    <div v-else>
      <!-- Header -->
      <!-- Left: Title -->
      <div class="w-100 d-flex flex-column flex-md-row justify-content-between between mb-2">
        <div class="d-flex align-items-center">
          <b-button
            variant="link"
            to="/dashboard"
            class="p-1"
          >
            <feather-icon
              icon="ArrowLeftIcon"
              size="24"
              class="text-secondary mt-n1"
            />
          </b-button>

          <p class="text-secondary d-sm-block d-md-none h4">
            Información Dispositivo
          </p>
          <p class="text-secondary d-none d-md-block d-lg-none h3">
            Información Dispositivo
          </p>
          <p class="text-secondary d-none d-lg-block h2">
            Información Dispositivo
          </p>
        </div>

        <!-- Right: Routing -->
        <div class="d-none d-md-block">
          <b-breadcrumb>
            <b-breadcrumb-item to="dashboard">
              <feather-icon
                icon="HomeIcon"
                size="16"
              />
              <span style="padding-left: 4px"> Dashboard </span>
            </b-breadcrumb-item>
            <b-breadcrumb-item active>
              Dispositivo
            </b-breadcrumb-item>
          </b-breadcrumb>
        </div>
      </div>

      <b-container>
        <b-row class="mb-2">
          <!-- Device Card -->
          <b-col
            cols="12"
            xl="4"
            class="d-flex flex-column"
          >
            <b-card class="h-80 shadow border border-secondary rounded">
              <b-card-header>
                <b-card-title class="mb-0">
                  Información Dispositivo
                </b-card-title>
              </b-card-header>
              <b-card-body class="mb-0">
                <b-overlay
                  :show="loading"
                  :variant="isDarkMode ? 'dark' : 'light'"
                >
                  <template v-slot:overlay>
                    <loading-logo />
                  </template>
                  <div>
                    <b-card-text class="d-flex justify-content-between">
                      <strong>
                        <feather-icon
                          icon="PowerIcon"
                          size="14"
                        />
                        Estado:
                      </strong>
                      <span>
                        <b-badge
                          :variant="device.alertCounts && device.alertCounts.statusAlerts > 0 ? 'danger' : 'success'"
                          :name="device.name"
                        >
                          {{ device.alertCounts && device.alertCounts.statusAlerts > 0 ? "Desconectado" : "En linea" }}
                        </b-badge>
                      </span>
                    </b-card-text>
                    <b-card-text class="d-flex justify-content-between">
                      <strong>
                        <feather-icon
                          icon="TagIcon"
                          size="14"
                        />
                        Nombre:
                      </strong>
                      <span class="truncate">{{ device.name }}</span>
                    </b-card-text>
                    <b-card-text class="d-flex justify-content-between">
                      <strong>
                        <feather-icon
                          icon="CloudIcon"
                          size="14"
                        />
                        Cloud ID:
                      </strong>
                      <span
                        id="tooltip-cloud-id"
                        class="truncate"
                      >{{ device.cloudId }}</span>
                      <b-tooltip
                        target="tooltip-cloud-id"
                        :title="device.cloudId"
                        class="cursor-pointer"
                      />
                    </b-card-text>
                    <b-card-text class="d-flex justify-content-between">
                      <strong>
                        <feather-icon
                          icon="AirplayIcon"
                          size="14"
                        />
                        Modelo:
                      </strong>
                      <span class="truncate">{{ device && device.model ? device.model.name : 'N/A' }}</span>
                    </b-card-text>
                    <b-card-text class="d-flex justify-content-between">
                      <strong>
                        <feather-icon
                          icon="MapPinIcon"
                          size="14"
                        />
                        Ubicación:
                      </strong>
                      <span class="truncate">{{ device && device.site ? device.site.name : 'N/A' }}</span>
                    </b-card-text>
                    <b-card-text class="d-flex justify-content-between">
                      <strong>
                        <feather-icon
                          icon="ClockIcon"
                          size="14"
                        />
                        Intervalo:
                      </strong>
                      <span style="padding-left: 5px; padding-right: 0px">
                        {{ (device.timeInterval % 60 === 0 ? (device.timeInterval / 60) : (device.timeInterval / 60).toFixed(1)) }} min
                      </span>
                    </b-card-text>
                  </div>

                </b-overlay>
              </b-card-body>
            </b-card>
          </b-col>

          <!-- Device Alerts -->
          <b-col
            cols="12"
            xl="8"
            class="d-flex flex-column"
          >
            <b-card class="h-100 shadow border border-secondary rounded mt-0">
              <alerts-by-device :device-id="deviceId" />
            </b-card>
          </b-col>

          <!-- Graph -->
          <b-col
            cols="12"
            lg="12"
            md="12"
            class="d-flex flex-column"
          >
            <b-card class="h-100 shadow border border-secondary rounded pb-0">
              <div class="b-card-header">
                <b-row class="d-flex justify-content-between align-items-center">
                  <b-col
                    cols="12"
                    md="auto"
                  >
                    <b-card-title class="mt-0 ml-2">
                      Datos de {{ `${dataName} ${sensorType === 'number' ? `(${chartUm})` : ''}` }}
                    </b-card-title>
                  </b-col>
                  <b-col
                    cols="12"
                    md="auto"
                  >
                    <date-picker
                      class="mb-1"
                      @update="updateChartData"
                    />
                  </b-col>
                </b-row>
              </div>
              <map-gps
                v-if="sensorType === 'gps'"
                :positions="chartSeries"
                :loading.sync="loading"
                map-height="500px"
                map-full-screen-size="80vh"
              />
              <apex-boolean-chart
                v-else-if="sensorType === 'boolean'"
                class="mb-0"
                :iot-data="chartSeries"
                :on-text="onText"
                :off-text="offText"
                :on-color="onColor"
                :off-color="offColor"
                :loading.sync="loading"
              />
              <apex-line-chart
                v-else-if="sensorType === 'number'"
                class="mb-0"
                :iot-data="chartSeries"
                :chart-color="chartColor"
                :chart-um="chartUm"
                :loading.sync="loading"
              />
              <apex-bars-chart
                v-else-if="sensorType === 'list'"
                class="mb-0"
                :iot-data="chartSeries"
                :loading.sync="loading"
                :list="listValues"
              />
            </b-card>
          </b-col>

          <!-- Table Graph -->
          <b-col
            cols="12"
            lg="12"
            md="12"
            class="d-flex flex-column"
          >
            <b-card class="shadow border border-secondary rounded mt-0 mb-0">
              <simple-table
                :items="iotDataTable"
                :loading.sync="loading"
                :per-page="5"
              />
            </b-card>
          </b-col>
        </b-row>
      </b-container>
    </div>
  </section>
</template>

<script>
import {
  BCardTitle,
  BContainer,
  BRow,
  BCol,
  BCard,
  BCardHeader,
  BCardBody,
  BCardText,
  BBadge,
  BButton,
  BBreadcrumb,
  BBreadcrumbItem,
  BTooltip,
  BSpinner,
  BOverlay,
} from 'bootstrap-vue'
import store from '@/store'
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue'
import ApexLineChart from '@/views/utils/ApexLineChart.vue'
import MapGps from '@/views/utils/MapGps.vue'
import ApexBooleanChart from '@/views/utils/ApexBooleanChart.vue'
import ApexBarsChart from '@/views/utils/ApexBarsChart.vue'
import DatePicker from '@/views/utils/DatePicker.vue'
import SimpleTable from '@/views/utils/SimpleTable.vue'
import moment from 'moment-timezone'
import allFetchData from './allFetchData'
import AlertsByDevice from '../alerts/history/AlertsByDevice.vue'
import LoadingLogo from '../miscellaneous/LoadingLogo.vue'

export default {
  components: {
    BCardTitle,
    BSpinner,
    BContainer,
    BRow,
    BCol,
    BCard,
    BCardHeader,
    BCardBody,
    BCardText,
    BBadge,
    BButton,
    BBreadcrumb,
    BBreadcrumbItem,
    BTooltip,
    FeatherIcon,
    ApexLineChart,
    MapGps,
    ApexBooleanChart,
    ApexBarsChart,
    DatePicker,
    SimpleTable,
    AlertsByDevice,
    BOverlay,
    LoadingLogo,
  },
  setup() {
    const { fetchDeviceById, fetchIotDataChart, loading } = allFetchData()
    return { fetchDeviceById, fetchIotDataChart, loading }
  },
  data() {
    return {
      loadingData: true,
      device: {},
      iotDataTable: [],
      deviceId: 0,
      deviceDataKey: '',
      showInterval: false,
      chartSeries: [],
      deviceLastDate: '',
      chartColor: '#FFF',
      onValue: '',
      onText: '',
      onColor: '',
      offValue: '',
      offText: '',
      offColor: '',
      listValues: {},
      chartUm: 'UM',
      rangeGraph: 'default',
      dataName: '',
      deviceOnline: false,
      sensorType: null,
    }
  },
  computed: {
    isDarkMode() {
      return store.state.appConfig.layout.skin === 'dark'
    },
  },
  watch: {
    deviceLastDate() {
      try {
        const futureTime = new Date(this.deviceLastDate)
        futureTime.setMinutes(futureTime.getMinutes() + 60)
        this.deviceOnline = new Date() < futureTime
      } catch {
        this.deviceOnline = false
      }
    },
  },
  mounted() {
    this.deviceId = parseInt(this.$route.params.id, 10)
    this.deviceDataKey = this.$route.params.data
    this.loadingData = false
    this.fetchDeviceById({ id: this.deviceId, dataKey: this.deviceDataKey }, this.setData)
    this.$socket.on('new_data', this.handleNewData)
  },
  methods: {
    setData(data) {
      this.device = data
      const deviceData = this.device.deviceData[0] || {}
      this.dataName = deviceData.name || 'Datos'
      this.sensorType = deviceData.sensorType || null
      this.chartSeries = this.sensorType === 'gps' ? this.device.iotData : [{ name: this.dataName, data: this.device.iotData }]
      this.deviceLastDate = this.device.iotData[0]?.x || ''
      this.setValuesBySensorType()
      this.setIotDataTable(this.device.iotData)
    },
    setIotDataTable(data) {
      this.iotDataTable = data.map(item => ({
        Fecha: moment.utc(item.x).local().format('DD/MM/YYYY hh:mm:ss a'),
        [this.dataName]: this.sensorType === 'boolean' 
        ? (item.y === 1 ? this.onText : this.offText) 
        : this.sensorType === 'list'
        ? this.listValues.list.find(listItem => listItem.key === item.val)?.text || item.val
        : item.y,
      })).reverse();
    },
    setValuesBySensorType() {
      const deviceData = this.device.deviceData[0] || {}
      if (this.sensorType === 'boolean') {
        this.onValue = deviceData.onValue
        this.onText = deviceData.onText
        this.onColor = deviceData.onColor
        this.offValue = deviceData.offValue
        this.offText = deviceData.offText
        this.offColor = deviceData.offColor
      }else if ( this.sensorType === 'list') {
        this.listValues = deviceData.list
      } 
      else {
        this.chartColor = deviceData.color || '#000000'
        this.chartUm = deviceData.um?.unit_type || 'UM'
      }
    },
    handleNewData(data) {
      if ((this.rangeGraph != 'custom') && data.device === this.device.cloudId && data.data.hasOwnProperty(this.deviceDataKey)) {
        
        const dataValue = data.data[this.deviceDataKey];
        const newDate = moment(data.createdAt).utc().toISOString()
        const newDataChart = { x: newDate }
        const newDataTable = { Fecha: moment(newDate).utc().format('DD/MM/YYYY hh:mm:ss a') }

        switch (this.sensorType){
          case 'boolean':{
            const isActive = dataValue === this.onValue;
            newDataChart.y = isActive ? 1 : -1;
            newDataChart.fillColor = isActive ? this.onColor : this.offColor;
            newDataTable[this.dataName] = isActive ? this.onText : this.offText;
            break;
          }
          case 'list': {
            const listItem = this.listValues.list.find(item => item.key === dataValue); 
            newDataChart.y = 1;
            newDataChart.val = dataValue;
            newDataChart.fillColor = listItem?.color ?? '#CCCCCC';
            newDataTable[this.dataName] = listItem?.text ?? dataValue;
            break;
          }
          case 'number': {
            newDataChart.y = parseFloat(dataValue);
            break;
          }
        }
        const newData = [...this.chartSeries[0].data, newDataChart];
        this.chartSeries[0].data = newData;
        this.iotDataTable.unshift(newDataTable)
      }
    },
    close() {
      this.dialogEdit = this.dialogExport = this.dialogDateRange = false
    },
    updateChartData(startDate, endDate, rangeType) {
      const params = {
        cloudId: this.device.cloudId,
        dataKey: this.deviceDataKey,
        startDate,
        endDate,
        rangeType,
        sensorType: this.sensorType,
        onValue: this.onValue,
        onColor: this.onColor,
        offValue: this.offValue,
        offColor: this.offColor,
        listValues: JSON.stringify(this.listValues),
      }
      this.fetchIotDataChart(params, res => {
        this.chartSeries = this.sensorType === 'gps' ? res : [{ ...this.chartSeries[0], data: res }]
        this.setIotDataTable(res)
        this.rangeGraph = rangeType
      })
    },
  },
}
</script>

<style scoped>
.truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 150px; /* Default max-width */
}

@media (max-width: 576px) {
  .truncate {
    max-width: 120px; /* Mobile portrait view */
  }
}

@media (max-width: 576px) and (orientation: landscape) {
  .truncate {
    max-width: 180px; /* Mobile landscape view */
  }
}

@media (min-width: 576px) and (max-width: 768px) {
  .truncate {
    max-width: 200px; /* Small devices (landscape phones, 576px and up) */
  }
}

@media (min-width: 768px) and (max-width: 992px) {
  .truncate {
    max-width: 250px; /* Medium devices (tablets, 768px and up) */
  }
}

@media (min-width: 992px) and (max-width: 1200px) {
  .truncate {
    max-width: 300px; /* Large devices (desktops, 992px and up) */
  }
}

@media (min-width: 1200px) {
  .truncate {
    max-width: none; /* Extra large devices (large desktops, 1200px and up) */
  }
}
</style>
